/**
 * `rescan-cloudeq-entity` — FR-27, NFR-06, NFR-07, CR-01. R-91-1 / D-91-7
 * (Gerry, 15 Sep 2026) · CLM-91.
 *
 * The admin **Rescan** over existing contracts — the "scan the corpus" Gerry
 * asked for. Inline, bounded (`limit` ≤ 500), one `withActor` transaction: read
 * the registry once, lock the candidate rows (`full_text IS NOT NULL AND
 * cloudeq_entity_source IS DISTINCT FROM 'admin'`, scoped, `FOR UPDATE`), run
 * the pure matcher per row and issue **one UPDATE per contract whose value or
 * note changes** — one `tg_contract_audit` row each, none for unchanged rows
 * (the `IS DISTINCT FROM` guard is in the statement). `dry_run: true` reports
 * the counts and the ambiguous list and writes nothing. Nothing is downloaded,
 * converted or sent to a model — the text is already in the column.
 *
 * Gate `contract:set_cloudeq_entity` — admin only.
 */
import { z } from 'zod';
import { CloudeqEntityKeySchema } from '../domain/cloudeq-entity.js';
import { ContractKeySchema } from '../primitives.js';
const Count = z.number().int().min(0);
export const RescanCloudeqEntityRequestSchema = z
    .object({
    /** Restrict the rescan to these contracts; default = every candidate in scope up to `limit`. */
    contract_keys: z.array(ContractKeySchema).min(1).max(200).optional(),
    /** Upper bound on the rows scanned in this call; 1..500, default 200. */
    limit: z.number().int().min(1).max(500).default(200),
    /** Report only — no UPDATE, no audit row. */
    dry_run: z.boolean().default(false),
})
    .strict();
export const RescanCloudeqEntityAmbiguousSchema = z
    .object({ contract_key: ContractKeySchema, candidates: z.array(CloudeqEntityKeySchema) })
    .strict();
export const RescanCloudeqEntityResponseSchema = z
    .object({
    dry_run: z.boolean(),
    /** Candidate rows read; `scanned = changed + unchanged`. */
    scanned: Count,
    /** Rows whose key or note would change (dry run) or did change (one audit row each). */
    changed: Count,
    unchanged: Count,
    /** Admin-sourced rows in scope — never candidates, reported for the receipt. */
    skipped_admin: Count,
    /** Matched entity → number of scanned rows resolving to it (after the scan). */
    by_entity: z.record(CloudeqEntityKeySchema, Count),
    ambiguous: z.array(RescanCloudeqEntityAmbiguousSchema).max(500),
    /** Active registry rows the scan used. */
    registry_size: Count,
})
    .strict();
//# sourceMappingURL=rescan-cloudeq-entity.js.map